(() => {
  if (customElements.get('infinite-scroll')) return;

  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.isLoading = false;
      this.currentPage = parseInt(this.dataset.currentPage, 10);
      this.totalPages = parseInt(this.dataset.totalPages, 10);
      this.sectionId = this.dataset.sectionId;
      this.collectionUrl = this.dataset.collectionUrl;
      this.storageKey = `infinite-scroll:${window.location.pathname}`;
      this.debouncedSaveScroll = debounce(() => this.saveScrollPosition(), 300);
    }

    connectedCallback() {
      if (this.currentPage >= this.totalPages) {
        this.classList.add('is-finished');
        return;
      }

      const saved = this.getSavedState();
      const isBackNav = (performance.getEntriesByType('navigation')[0] || {}).type === 'back_forward';

      if (isBackNav && saved && saved.loadedPages > 1 && saved.search === window.location.search) {
        this.restore(saved);
      } else {
        this.clearSavedState();
        this.initObserver();
      }

      window.addEventListener('scroll', this.debouncedSaveScroll);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      window.removeEventListener('scroll', this.debouncedSaveScroll);
    }

    initObserver() {
      this.observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.isLoading) {
              this.loadNextPage();
            }
          });
        },
        { rootMargin: '200px' }
      );
      this.observer.observe(this);
    }

    async loadNextPage() {
      if (this.isLoading || this.currentPage >= this.totalPages) return;

      this.isLoading = true;
      this.classList.add('is-loading');

      const nextPage = this.currentPage + 1;

      try {
        const html = await this.fetchPage(nextPage);
        this.appendProducts(html);
        this.currentPage = nextPage;
        this.saveState();

        if (this.currentPage >= this.totalPages) {
          this.classList.add('is-finished');
          this.classList.remove('is-loading');
          if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
          }
        }
      } catch (e) {
        console.error('Infinite scroll: failed to load page', nextPage, e);
      } finally {
        this.isLoading = false;
        this.classList.remove('is-loading');
      }
    }

    async fetchPage(page) {
      const url = new URL(this.collectionUrl || window.location.pathname, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);

      // Preserve active filters/sort from the current page URL.
      currentParams.forEach((value, key) => {
        if (key === 'page' || key === 'section_id') return;
        url.searchParams.set(key, value);
      });

      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('page', page);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.text();
    }

    appendProducts(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = parsed.getElementById('product-grid');
      if (!newGrid) return;

      const productGrid = document.getElementById('product-grid');
      if (!productGrid) return;

      const fragment = document.createDocumentFragment();
      Array.from(newGrid.children).forEach(child => {
        fragment.appendChild(child.cloneNode(true));
      });
      productGrid.appendChild(fragment);
    }

    async restore(saved) {
      this.classList.add('is-loading');

      const pagesToLoad = [];
      for (let p = 2; p <= saved.loadedPages; p++) {
        pagesToLoad.push(p);
      }

      try {
        const results = await Promise.all(
          pagesToLoad.map(p => this.fetchPage(p))
        );

        results.forEach(html => this.appendProducts(html));
        this.currentPage = saved.loadedPages;
        this.saveState();

        if (saved.scrollY) {
          requestAnimationFrame(() => {
            window.scrollTo(0, saved.scrollY);
          });
        }
      } catch (e) {
        console.error('Infinite scroll: failed to restore pages', e);
        this.clearSavedState();
      } finally {
        this.classList.remove('is-loading');

        if (this.currentPage < this.totalPages) {
          this.initObserver();
        } else {
          this.classList.add('is-finished');
        }
      }
    }

    saveState() {
      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            loadedPages: this.currentPage,
            scrollY: window.scrollY,
            search: window.location.search
          })
        );
      } catch (e) {
        // sessionStorage may be unavailable
      }
    }

    saveScrollPosition() {
      if (this.currentPage <= 1) return;
      this.saveState();
    }

    getSavedState() {
      try {
        const data = sessionStorage.getItem(this.storageKey);
        return data ? JSON.parse(data) : null;
      } catch (e) {
        return null;
      }
    }

    clearSavedState() {
      try {
        sessionStorage.removeItem(this.storageKey);
      } catch (e) {
        // sessionStorage may be unavailable
      }
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
})();
